import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Form } from "formik";
import * as yup from "yup";
import FullWidthCardContainer from "../../../../commons/dashboard/fullWidthCardContainer";
import MyResponsiveBarMonths from "../../../../commons/dashboard/myResponsiveBarMonths";
import MyResponsiveLine from "../../../../commons/dashboard/myResponsiveLine";
import FormikDatePicker from "../../../../commons/formik/formikDatePicker";
import FormikForm from "../../../../commons/formik/formikForm";
import { StyledTraslados } from "../../../../styled/traslados";
import ReportesChartLegend from "./reportesChartLegend";
import {
  createReportesBarTooltip,
  createReportesLineTooltip,
} from "./reportesChartTooltip";
import {
  formatTramosPorTraslado,
  getEstadoChartColor,
  getEstadoLegendItems,
  getIntegerTickValues,
  getSituacionActualResumen,
  mapSituacionActualToBarMonths,
  mapSituacionPeriodoToLine,
} from "./reportesChartUtils";

const INTEGER_AXIS_LEFT = {
  tickSize: 3,
  tickPadding: 11,
  tickRotation: 0,
  legend: "",
  legendPosition: "middle",
  legendOffset: -47,
  format: (value) => (Number.isInteger(Number(value)) ? value : ""),
};

const CHART_TOOLTIP_THEME = {
  tooltip: {
    wrapper: { zIndex: 1500 },
    container: { zIndex: 1500, overflow: "visible" },
  },
};

const periodValidationSchema = yup.object().shape({
  fechaDesde: yup
    .date()
    .nullable()
    .required("Campo obligatorio")
    .test(
      "date-from-before-to",
      "La fecha desde debe ser anterior o igual a la fecha hasta",
      function (value) {
        const { fechaHasta } = this.parent;
        if (!value || !fechaHasta) return true;
        return new Date(value) <= new Date(fechaHasta);
      },
    ),
  fechaHasta: yup
    .date()
    .nullable()
    .required("Campo obligatorio")
    .test(
      "date-to-after-from",
      "La fecha hasta debe ser posterior o igual a la fecha desde",
      function (value) {
        const { fechaDesde } = this.parent;
        if (!value || !fechaDesde) return true;
        return new Date(value) >= new Date(fechaDesde);
      },
    ),
});

const ReportesTraslados = ({
  situacionActual,
  situacionPeriodo,
  periodInitialValues,
  onPeriodChange,
  onRefreshActual,
  onRefreshPeriodo,
  isBackofficeRole,
  arts,
  artsLoading,
  selectedArtId,
  onArtChange,
}) => {
  const {
    data: actualChartData,
    keys: actualChartKeys,
    estados: actualEstados,
  } = mapSituacionActualToBarMonths(situacionActual.data);
  const actualResumen = getSituacionActualResumen(situacionActual.data);
  const periodoChartData = mapSituacionPeriodoToLine(
    situacionPeriodo.data,
    periodInitialValues.fechaDesde,
    periodInitialValues.fechaHasta,
  );
  const legendItems = getEstadoLegendItems(actualEstados);
  const actualTicks = getIntegerTickValues(
    ...actualChartData.map((row) => row.value ?? 0),
  );
  const periodoTicks = getIntegerTickValues(
    ...periodoChartData.flatMap((serie) =>
      (serie.data || []).map((point) => point.y ?? 0),
    ),
  );
  const actualChartHeight = Math.max(400, actualEstados.length * 28 + 168);
  const periodChartHeight = 460;
  const actualLeftMargin = Math.min(
    280,
    Math.max(160, ...actualEstados.map((nombre) => nombre.length * 6.5)),
  );

  return (
    <div>
      <StyledTraslados.Title variant="h6">Reportes de traslados</StyledTraslados.Title>

      {isBackofficeRole && (
        <StyledTraslados.Paper>
          <FormControl variant="standard" sx={{ minWidth: 240 }} disabled={artsLoading}>
            <InputLabel id="reportes-traslados-art-label">Art</InputLabel>
            <Select
              labelId="reportes-traslados-art-label"
              value={selectedArtId || ""}
              label="Art"
              onChange={(event) => onArtChange(event.target.value || null)}>
              {arts.map((art) => (
                <MenuItem value={art.id} key={art.id}>
                  {art.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </StyledTraslados.Paper>
      )}

      <StyledTraslados.ReportsGrid>
        <FullWidthCardContainer
          text="SITUACIÓN DE TRASLADOS ACTUAL"
          chartHeight={actualChartHeight}
          component={
            <StyledTraslados.ChartColumn>
              <StyledTraslados.ChartMetrics>
                <StyledTraslados.ChartMetric>
                  <StyledTraslados.ChartMetricLabel>
                    Tramos
                  </StyledTraslados.ChartMetricLabel>
                  <StyledTraslados.ChartMetricValue>
                    {actualResumen.cantidadTramos}
                  </StyledTraslados.ChartMetricValue>
                </StyledTraslados.ChartMetric>
                <StyledTraslados.ChartMetric>
                  <StyledTraslados.ChartMetricLabel>
                    Traslados
                  </StyledTraslados.ChartMetricLabel>
                  <StyledTraslados.ChartMetricValue>
                    {actualResumen.cantidadTraslados}
                  </StyledTraslados.ChartMetricValue>
                </StyledTraslados.ChartMetric>
                <StyledTraslados.ChartMetric>
                  <StyledTraslados.ChartMetricLabel>
                    Ratio tramos/traslado
                  </StyledTraslados.ChartMetricLabel>
                  <StyledTraslados.ChartMetricValue>
                    {formatTramosPorTraslado(actualResumen.ratio)}
                  </StyledTraslados.ChartMetricValue>
                </StyledTraslados.ChartMetric>
              </StyledTraslados.ChartMetrics>
              <StyledTraslados.ChartWithLegend>
                <StyledTraslados.ChartPlot>
                  <MyResponsiveBarMonths
                    data={actualChartData}
                    keys={actualChartKeys}
                    type="date"
                    layout="horizontal"
                    padding={0.58}
                    colors={(bar) => getEstadoChartColor(bar.indexValue)}
                    legends={[]}
                    margin={{
                      top: 8,
                      right: 48,
                      bottom: 24,
                      left: actualLeftMargin,
                    }}
                    axisBottom={{
                      tickSize: 0,
                      tickPadding: 0,
                      tickRotation: 0,
                      format: () => "",
                    }}
                    axisLeft={{
                      tickSize: 0,
                      tickPadding: 8,
                      tickRotation: 0,
                    }}
                    enableGridX={false}
                    enableGridY={false}
                    enableLabel
                    label={(datum) => (datum.value > 0 ? datum.value : "")}
                    labelPosition="end"
                    labelOffset={10}
                    labelTextColor="#424242"
                    yScale={{
                      type: "linear",
                      min: 0,
                      max: actualTicks[actualTicks.length - 1],
                      nice: false,
                    }}
                    tooltip={createReportesBarTooltip(actualResumen.cantidadTramos)}
                    theme={CHART_TOOLTIP_THEME}
                  />
                </StyledTraslados.ChartPlot>
              </StyledTraslados.ChartWithLegend>
            </StyledTraslados.ChartColumn>
          }
          isFetching={situacionActual.isFetching}
          fetchData={onRefreshActual}
          didInvalidate={situacionActual.didInvalidate}
        />

        <FullWidthCardContainer
          text="SITUACIÓN DE TRASLADOS POR PERÍODO"
          chartHeight={periodChartHeight}
          component={
            <StyledTraslados.ChartColumn>
              <FormikForm
                initialValues={periodInitialValues}
                enableReinitialize
                validationSchema={periodValidationSchema}
                onSubmit={onPeriodChange}>
                {({ values, validateForm, setTouched }) => {
                  const trySubmitPeriod = async (nextValues) => {
                    const errors = await validateForm(nextValues);
                    setTouched({ fechaDesde: true, fechaHasta: true });
                    if (!errors.fechaDesde && !errors.fechaHasta) {
                      onPeriodChange(nextValues);
                    }
                  };

                  return (
                    <Form>
                      <StyledTraslados.DateFilters>
                        <FormikDatePicker
                          name="fechaDesde"
                          label="Fecha Desde"
                          callBack={(fechaDesde) =>
                            trySubmitPeriod({ ...values, fechaDesde })
                          }
                        />
                        <FormikDatePicker
                          name="fechaHasta"
                          label="Fecha Hasta"
                          callBack={(fechaHasta) =>
                            trySubmitPeriod({ ...values, fechaHasta })
                          }
                        />
                      </StyledTraslados.DateFilters>
                    </Form>
                  );
                }}
              </FormikForm>
              <StyledTraslados.ChartWithLegend>
                <StyledTraslados.ChartPlot>
                  <MyResponsiveLine
                    data={periodoChartData}
                    preserveOrder
                    curve="linear"
                    yMin={0}
                    colors={getEstadoChartColor}
                    legends={[]}
                    margin={{ top: 12, right: 16, bottom: 50, left: 60 }}
                    yScale={{
                      type: "linear",
                      min: 0,
                      max: periodoTicks[periodoTicks.length - 1],
                      stacked: false,
                      nice: false,
                    }}
                    axisLeft={{
                      ...INTEGER_AXIS_LEFT,
                      tickValues: periodoTicks,
                    }}
                    gridYValues={periodoTicks}
                    tooltip={createReportesLineTooltip(periodoChartData)}
                    theme={CHART_TOOLTIP_THEME}
                  />
                </StyledTraslados.ChartPlot>
                <ReportesChartLegend items={legendItems} />
              </StyledTraslados.ChartWithLegend>
            </StyledTraslados.ChartColumn>
          }
          isFetching={situacionPeriodo.isFetching}
          fetchData={onRefreshPeriodo}
          didInvalidate={situacionPeriodo.didInvalidate}
        />
      </StyledTraslados.ReportsGrid>
    </div>
  );
};

export default ReportesTraslados;
